// Pure helpers for the engineer PWA home dashboard (Session AFF). All date maths is LOCAL to the
// device (an engineer reads "today" as the day showing on their phone, never UTC), and every
// function takes an explicit `now: Date` so it is deterministic and unit-testable. A job is only
// counted as work-in-hand while it is scheduled or in progress: a submitted / completed job has
// left the engineer's plate and is never "today", "this week" or "overdue".

// The minimal shape these helpers read. The real JobView carries far more, but the counts and the
// day-grouping only ever look at when a job is scheduled and whether it is still open.
export interface DashboardJobLike {
  scheduledFor: string | null;
  status: string;
}

// "Good morning" before noon (device local time), "Good afternoon" from 12:00 on. The first name is
// appended by the caller, so this returns the bare greeting for a clean unit test of the boundary.
export function greetingForDate(now: Date): string {
  return now.getHours() < 12 ? "Good morning" : "Good afternoon";
}

// The engineer's first name from their display name: the first whitespace-separated token. Returns
// "" when there is nothing usable, so the caller can fall back to a name-less greeting.
export function firstNameFromDisplayName(displayName: string | null | undefined): string {
  if (!displayName) return "";
  const first = displayName.trim().split(/\s+/)[0];
  return first ?? "";
}

// A job the engineer still has to attend. A submitted / pending-approval / complete / cancelled job
// is not. Session AQY (D-AQX-1): a PAUSED job (awaiting a part) still is -- it stays on the phone's
// own day/week counts and "No date set" group exactly as an ordinary open job does. The same three
// statuses ACTIVE_STATUSES (server, jobs/job.routes.ts) and POP_TAG_OPEN_STATUSES
// (server, engineer/popTagMatch.ts) hold; popTagMatch.test.ts pins all three against each other.
export function isOpenJob(status: string): boolean {
  return status === "scheduled" || status === "in_progress" || status === "parts_pending";
}

// The start of the local calendar day that `d` falls in (midnight, device local time).
function localDayStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Parse a stored ISO instant, or null when absent / unparseable.
function parseIso(iso: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

// The local-day key (midnight ms) a job is scheduled on, or null when it has no valid date.
function scheduledDayMs(job: DashboardJobLike): number | null {
  const d = parseIso(job.scheduledFor);
  return d ? localDayStart(d).getTime() : null;
}

// The Monday that opens the local week containing `now` (weeks run Monday to Sunday).
function mondayOf(now: Date): Date {
  const day = localDayStart(now);
  const dow = day.getDay(); // 0 = Sunday ... 6 = Saturday
  const sinceMonday = (dow + 6) % 7; // Monday -> 0, Sunday -> 6
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() - sinceMonday);
}

// Jobs scheduled on the same local calendar day as `now` and still open.
export function countJobsToday<T extends DashboardJobLike>(jobs: T[], now: Date): number {
  const today = localDayStart(now).getTime();
  return jobs.filter((j) => isOpenJob(j.status) && scheduledDayMs(j) === today).length;
}

// Jobs scheduled within the local Monday-to-Sunday week containing `now` and still open.
export function countJobsThisWeek<T extends DashboardJobLike>(jobs: T[], now: Date): number {
  const start = mondayOf(now).getTime();
  const end = start + 7 * 24 * 60 * 60 * 1000;
  return jobs.filter((j) => {
    if (!isOpenJob(j.status)) return false;
    const ms = scheduledDayMs(j);
    return ms != null && ms >= start && ms < end;
  }).length;
}

// Jobs scheduled on a local day STRICTLY BEFORE today and still open: a visit that should already
// have happened. A job scheduled earlier today is not overdue (the day is not yet out); a job with
// no date never counts.
export function countOverdue<T extends DashboardJobLike>(jobs: T[], now: Date): number {
  const today = localDayStart(now).getTime();
  return jobs.filter((j) => {
    if (!isOpenJob(j.status)) return false;
    const ms = scheduledDayMs(j);
    return ms != null && ms < today;
  }).length;
}

// The overdue jobs themselves (the same rule as countOverdue), oldest first, so the home screen can
// list them directly: an engineer reaches an overdue job in one tap, as they do today's (Session BAS).
export function openJobsOverdue<T extends DashboardJobLike>(jobs: T[], now: Date): T[] {
  const today = localDayStart(now).getTime();
  return jobs
    .filter((j) => {
      if (!isOpenJob(j.status)) return false;
      const ms = scheduledDayMs(j);
      return ms != null && ms < today;
    })
    .sort((a, b) => (a.scheduledFor ?? "").localeCompare(b.scheduledFor ?? ""));
}

// The open jobs scheduled on a given local day, earliest first. Used for the home "Today" list.
export function openJobsOnDay<T extends DashboardJobLike>(jobs: T[], day: Date): T[] {
  const key = localDayStart(day).getTime();
  return jobs
    .filter((j) => isOpenJob(j.status) && scheduledDayMs(j) === key)
    .sort((a, b) => (a.scheduledFor ?? "").localeCompare(b.scheduledFor ?? ""));
}

// Open jobs with no usable scheduled date: a repair, a condition report or a site inspection can be
// raised with none at all, and a stored date string that fails to parse behaves the same way as far
// as the engineer is concerned -- scheduledDayMs returns null for both, so neither ever lands in
// "Today" or "Coming up" (both key off a real calendar day). Used by the home screen to surface this
// work under its own "No date set" group rather than leaving it findable only via "Unscheduled" on
// My Jobs (Session APF, PWA audit finding 11).
export function openJobsWithNoDate<T extends DashboardJobLike>(jobs: T[]): T[] {
  return jobs.filter((j) => isOpenJob(j.status) && scheduledDayMs(j) === null);
}

// The next `dayCount` local days after today, each with its open jobs (earliest first). Days with no
// open jobs are omitted, so the home "Coming up" section only shows days that have something on them.
export function upcomingDays<T extends DashboardJobLike>(
  jobs: T[],
  now: Date,
  dayCount: number,
): { date: Date; jobs: T[] }[] {
  const start = localDayStart(now);
  const out: { date: Date; jobs: T[] }[] = [];
  for (let i = 1; i <= dayCount; i++) {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const dayJobs = openJobsOnDay(jobs, date);
    if (dayJobs.length > 0) out.push({ date, jobs: dayJobs });
  }
  return out;
}
